import {Moment} from 'moment-timezone'

import {Iso8601} from './atomic.types'
import {RunningTimesEquipmentData} from './shiftHandover.types'

export enum KpiRangeBreachSeverity {
  Alarm = 'alarm',
  Warning = 'warning',
  Optimal = 'optimal'
}

export type KpiAssignmentDto = {
  kpiName: string
  result?: KpiResultDto
  timeFrameResults?: Record<CalculationTimeFrameType, KpiResultDto>
}

export type KpiResultDto = {
  value: string
  unit: string
  startTime: Iso8601
  endTime: Iso8601
  kpiBreachSeverity: KpiRangeBreachSeverity | null
}

export type KpiAssignment = {
  kpiName: string
  result?: KpiResult
  timeFrameResults?: Record<CalculationTimeFrameType, KpiResult>
}

export type KpiResult = {
  value: string
  unit: string
  startTime: Moment
  endTime: Moment
  kpiBreachSeverity: KpiRangeBreachSeverity | null
}

export type KpiPerformanceDto = {
  kpiAssignments: KpiAssignmentDto[]
}
export type KpiPerformance = {
  kpiAssignments: KpiAssignment[]
}

export type EquipmentPerformanceDto = {
  equipment: RunningTimesEquipmentData
  kpiPerformance?: KpiPerformanceDto
}

export type EquipmentPerformance = {
  equipment: RunningTimesEquipmentData
  kpiPerformance?: KpiPerformance
}

export type BaseManualKpi = {
  name: string
  unit: string
  processStage: string
  actualValue?: number
  plannedValue?: number
  delta?: number
}

export type ManualKpiDto = BaseManualKpi & {
  id: string
  timestamp: Iso8601
  createdAt: Iso8601
  updatedAt: Iso8601
  concurrencyStamp: string
  resolution: ManualKpiResolution
}

export type ManualKpi = BaseManualKpi & {
  id: string
  timestamp: Moment
  createdAt: Moment
  updatedAt: Moment
  concurrencyStamp: string
  resolution: ManualKpiResolution
}

export type ManualKpiToUpdate = BaseManualKpi & {
  id?: string // if id is supplied then its update of existing entity
  concurrencyStamp?: string
}

export enum ManualKpiResolution {
  Daily = 'Daily',
  PerShift = 'perShift'
}

export type ManualKpiSuggestion = {
  name: string
  unit: string
  processStage: string
}

export type KpiCalculationTimeFrameDto = {
  startDate: Iso8601
  endDate: Iso8601
  calculationTimeFrameType: CalculationTimeFrameType
}

export enum CalculationTimeFrameType {
  Today = 'today',
  Yesterday = 'yesterday',
  Last3Days = 'last3Days',
  Last7Days = 'last7Days',
  Last30Days = 'last30Days',
  CurrentMonth = 'currentMonth',
  LastMonth = 'lastMonth',
  Last24Hours = 'last24Hours',
  CurrentShift = 'currentShift',
  PastShift = 'pastShift',
  ShiftBeforePastShift = 'shiftBeforePastShift',
  TheDayBeforeYesterday = 'theDayBeforeYesterday',
  LastWeekend = 'lastWeekend',
  TheWeekendBeforeLast = 'theWeekendBeforeLast',
  MonthBeforeLastMonth = 'monthBeforeLastMonth',
  CustomDateRange = 'customDateRange'
}

export enum KpiType {
  ClinkerProduction = 'clinkerProduction',
  CementProduction = 'cementProduction',
  AlternativeFuelRate = 'alternativeFuelRate',
  KilnHeatConsumption = 'kilnHeatConsumption',
  TechnicalCementPowerConsumption = 'technicalCementPowerConsumption',
  ClinkerIncorporation = 'clinkerIncorporation',
  CO2EmissionsPerCement = 'co2EmissionsPerCement',
  MaintenanceCosts = 'maintenanceCosts',
  KilnReliabilityCoefficient = 'kilnReliabilityCoefficient',
  KilnOperatingCoefficient = 'kilnOperatingCoefficient',
  KilnMTBF = 'kilnMeanTimeBetweenFailure',
  FinishMillOperatingCoefficient = 'finishMillOperatingCoefficient',
  RawMillsSpPowerConsumption = 'rawMillsSpPowerConsumption',
  KilnSpPowerConsumption = 'kilnSpPowerConsumption',
  RawMillProductionRate = 'rawMillProductionRate',
  ClinkerProductionRate = 'clinkerProductionRate',
  CementMillsProductionRate = 'cementMillsProductionRate',
  CO2EmissionsPerClinker = 'co2EmissionsPerClinker',
  // per equipment
  KilnProcessCoefficientPerEquipment = 'kilnProcessCoefficientPerEquipment',
  KilnOperationCoefficientPerEquipment = 'kilnOperationCoefficientPerEquipment',
  FinishMillOutputPerEquipment = 'finishMillOutputPerEquipment',
  FinishMillSpecificPowerConsumptionPerEquipment = 'finishMillSpecificPowerConsumptionPerEquipment',
  FinishMillOperatingCoefficientPerEquipment = 'finishMillOperatingCoefficientPerEquipment',
  ClinkerProductionRatePerEquipment = 'clinkerProductionRatePerEquipment',
  AlternativeFuelRatePerEquipment = 'alternativeFuelRatePerEquipment',
  KilnHeatConsumptionPerEquipment = 'kilnHeatConsumptionPerEquipment',
  KilnSpecificPowerConsumptionMainDrivePerEquipment = 'kilnSpecificPowerConsumptionMainDrivePerEquipment',
  RawMillSpecificPowerConsumptionMainDrivePerEquipment = 'rawMillSpecificPowerConsumptionMainDrivePerEquipment',
  RawMillOutputPerEquipment = 'rawMillOutputPerEquipment',
  KilnReliabilityCoefficientPerEquipment = 'kilnReliabilityCoefficientPerEquipment',
  KilnMTBFPerEquipment = 'kilnMeanTimeBetweenFailurePerEquipment'
}
